import { format } from 'date-fns'
import { getDefaultOptions } from 'date-fns/getDefaultOptions'
import { formatInTimeZone as formatTZ, fromZonedTime } from 'date-fns-tz'

import { DEFAULT_COUNTRY } from '@ngb-frontend/shared/config'

import { getCountryL10nConfig } from './getCountryLocaleConfig'
import { DEFAULT_DATE_FNS_LOCALE } from '../localeConfig'

import type { QueryCountryCode } from '@ngb-frontend/shared/types'
import type { Locale } from 'date-fns'

type DateTimeFormatWidth = 'short' | 'medium' | 'long' | 'full'
type DateTimeFormatOpts = {
  width?: DateTimeFormatWidth
  formatTime?: boolean
  manualLocale?: Locale
}

type TimeZoneFormatOpts = {
  country?: QueryCountryCode
}

const DEFAULT_DATE_FORMAT_OPTS: DateTimeFormatOpts = {
  width: 'short',
  formatTime: false,
}

/**
 * Times are shown without seconds. The hour is left as the locale writes it,
 * so it is not padded: bg writes 9:00, where its medium width would give
 * 09:00:00.
 */
export const TIME_FORMAT = 'H:mm'

export const getDateTimeFormat = (
  opts: DateTimeFormatOpts = DEFAULT_DATE_FORMAT_OPTS,
) => {
  if (opts.formatTime) return TIME_FORMAT

  const nxtDateFnsLocale =
    opts.manualLocale || getDefaultOptions().locale || DEFAULT_DATE_FNS_LOCALE

  return nxtDateFnsLocale.formatLong?.date({
    // date-fns treats an absent width as 'full'; every caller here wants the
    // numeric pattern, and the default above only applies to a bare call.
    width: opts.width ?? 'short',
  })
}

const DEFAULT_DATE_TIME_OPTS: DateTimeFormatOpts = {
  width: 'medium',
  formatTime: false,
}

export const formatDateTime = (
  dateTime = '',
  opts: DateTimeFormatOpts = DEFAULT_DATE_TIME_OPTS,
) => {
  const nxtDateFnsLocale =
    opts.manualLocale || getDefaultOptions().locale || DEFAULT_DATE_FNS_LOCALE

  try {
    return dateTime
      ? format(
          new Date(dateTime),
          getDateTimeFormat({ ...opts, manualLocale: nxtDateFnsLocale }),
          { locale: nxtDateFnsLocale },
        )
      : '-'
  } catch {
    return dateTime
  }
}
export const formatInTimeZone = (
  utcDateTime = '',
  opts: TimeZoneFormatOpts,
) => {
  const l10NConfig = getCountryL10nConfig(opts.country ?? DEFAULT_COUNTRY) ?? {}
  const tz = l10NConfig.tz
  if (!tz) return utcDateTime

  try {
    return formatTZ(new Date(utcDateTime), tz, "yyyy-MM-dd'T'HH:mm:ss.000'Z'")
  } catch {
    return utcDateTime
  }
}

export const formatZonedToUTC = (
  zonedDateTime: Date,
  opts: TimeZoneFormatOpts,
) => {
  const l10NConfig = getCountryL10nConfig(opts.country ?? DEFAULT_COUNTRY) ?? {}
  const tz = l10NConfig.tz
  if (!tz) return zonedDateTime

  try {
    return fromZonedTime(new Date(zonedDateTime), tz)
  } catch {
    return zonedDateTime
  }
}
